import store from '../../redux/store';
import { getConsentStatus, CookieCategories } from '../../services/cookieConsentService';

const hasAnalyticsConsent = () => {
  const consent = getConsentStatus();
  return consent.timestamp && consent.categories[CookieCategories.ANALYTICS];
};

/**
 * Obtiene los datos del usuario y workspace actuales desde Redux
 * @returns {Object} Datos del contexto actual
 */
const getUserContext = () => {
  const state = store.getState();
  const { auth } = state;

  // Obtener el workspace actual
  const currentWorkspace = auth.workspacesList?.find((w) => w.id === auth.currentWorkspace);

  return {
    userEmail: auth.user?.email || auth.email,
    userName: auth.user ? `${auth.user.firstName || ''} ${auth.user.lastName || ''}`.trim() : '',
    userId: auth.user?.id,
    workspaceId: auth.currentWorkspace,
    workspaceName: currentWorkspace?.name || currentWorkspace?.placeholder,
    companyId: currentWorkspace?.companyId,
    companyName: currentWorkspace?.companyName,
    planName: auth.plan?.name || auth.planFeatures?.name,
    userRole: auth.currentRole,
  };
};

/**
 * Envía eventos a Google Tag Manager con datos automáticos del contexto
 * @param {string} action - Nombre del evento a trackear
 * @param {Object} customProps - Propiedades personalizadas del evento (opcionales)
 * @param {boolean} options.includeContext - Si incluir el contexto automáticamente (default: true)
 */
export const TrackEventGTM = (action, customProps = {}, options = {}) => {
  // Verificar que GTM esté habilitado
  if (process.env.RAZZLE_RUNTIME_ENABLE_GOOGLE_ANALYTICS !== 'true' || !hasAnalyticsConsent()) {
    return;
  }

  // Validar parámetros
  if (!action || typeof action !== 'string') {
    console.warn('TrackEventGTM: action debe ser un string no vacío');
    return;
  }

  // Obtener contexto automáticamente por defecto
  const includeContext = options.includeContext !== false;
  let eventData = { ...customProps };

  if (includeContext) {
    const context = getUserContext();

    // Combinar contexto con props personalizadas (las props personalizadas tienen prioridad)
    eventData = {
      ...context,
      ...customProps,
    };

    // Para compatibilidad hacia atrás, mantener userEmail si se pasa explícitamente
    if (customProps.userEmail) {
      eventData.userEmail = customProps.userEmail;
    }
  }

  // Obtener el email para el filtro de Alkemy
  const email = eventData.userEmail || '';

  // Filtrar emails internos de Alkemy
  if (email && email.toLowerCase().includes('alkemy')) {
    return;
  }

  // Usar dataLayer de GTM
  if (typeof window !== 'undefined' && window.dataLayer) {
    try {
      // Limpiar valores undefined o null del objeto
      const cleanedData = Object.entries(eventData).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const eventPayload = {
        event: action,
        ...cleanedData,
        timestamp: new Date().toISOString(),
      };

      window.dataLayer.push(eventPayload);
    } catch (error) {
      console.error('❌ Error al enviar evento a GTM:', error);
    }
  } else {
    if (process.env.NODE_ENV !== 'production') {
      // Log en desarrollo para debugging
      console.log('GTM Event (development):', action, eventData);
    }
  }
};
